import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useUpdateResource } from "../../../../hooks/useUpdateResource";
import LoadingButton from "../../../form/LoadingButton";
import SubmitButton from "../../../form/SubmitButton";

type Props = {
  storeId: number;
  onSubmitSuccess?: () => void;
};

type FormValues = {
  amount: number;
  note?: string;
  receipt?: File; // لإضافة صورة وصل التحويل
};

const StoreBalanceForm: React.FC<Props> = ({ storeId, onSubmitSuccess }) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<"withdraw_bank" | "manual_credit">(
    "withdraw_bank"
  );
  const { loading, updateResource } = useUpdateResource();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      amount: 0,
      note: "",
      receipt: undefined,
    },
  });

  const onSubmit = async (data: FormValues) => {
    const payload = {
      totalAmount: Number(data.amount),
      storeId,
      status: tab === "withdraw_bank" ? "SETTLEMENT" : "ADMIN_WITHDRAW",
    };

    const formData= new FormData();
    formData.append("totalAmount",`${data.amount}`);
    formData.append("status",payload.status)
    formData.append("storeId",`${storeId}`);
    if(data.note){
      formData.append("note",data.note)
    }
    if(data.receipt){
      formData.append("receipt",data.receipt)
    }

    await updateResource(`store-transaction/admin/transaction`, formData, "admin", {
      method: "POST",
      successMessage:
        tab === "manual_credit"
          ? t("balance_added_success")
          : t("balance_withdrawn_success"),
      invalidateQueries: ["store-transaction"],
    });

    reset();
    if (onSubmitSuccess) onSubmitSuccess();
  };

  return (
    <div className="min-w-[340px]">
      {/* Tabs */}
      <div className="flex flex-col gap-1 mb-4">
        <div className="flex justify-center border-b border-muted">
          <button
            type="button"
            onClick={() => setTab("withdraw_bank")}
            className={`px-4 py-2 font-medium transition-colors ${
              tab === "withdraw_bank"
                ? "border-b-2 border-danger text-danger"
                : "text-muted-foreground hover:text-danger"
            }`}
          >
            {t("tab_withdraw_bank")}
          </button>
          <button
            type="button"
            onClick={() => setTab("manual_credit")}
            className={`px-4 py-2 font-medium transition-colors ${
              tab === "manual_credit"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-primary"
            }`}
          >
            {t("tab_manual_credit")}
          </button>
        </div>
        {/* Tab Description */}
        <p className="text-sm text-muted text-center mt-1">
          {tab === "withdraw_bank"
            ? t("tab_withdraw_bank_desc")
            : t("tab_manual_credit_desc")}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
        {/* Amount Input */}
        <div>
          <label className="block text-sm font-medium mb-1">{t("amount")}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            {...register("amount", {
              required: t("enter_amount") || "Enter amount",
              min: { value: 0.01, message: t("invalid_amount") || "Invalid amount" },
            })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t("enter_amount") || "Enter amount"}
          />
          {errors.amount && (
            <p className="text-danger text-sm mt-1">{errors.amount.message}</p>
          )}
        </div>

        {/* Note Input */}
        <div>
          <label className="block text-sm font-medium mb-1">{t("note")}</label>
          <textarea
            {...register("note")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t("optional_note") || "Optional note..."}
            rows={3}
          />
        </div>

        {/* Receipt Upload (only for withdraw_bank) */}
        {tab === "withdraw_bank" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("upload_receipt") || "Upload Receipt"}
            </label>
           <input
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            {...register("receipt")}
            className="w-full"
          />
          </div>
        )}

        {/* Submit Button */}
        {loading ? (
          <LoadingButton />
        ) : (
          <SubmitButton
            label={tab === "manual_credit" ? t("add_balance") : t("withdraw_balance")}
          />
        )}
      </form>
    </div>
  );
};

export default StoreBalanceForm;
